import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { MsalInitService } from '../services/msal-init.service';
import { isStaleAuthCacheError } from '../utils/auth-error.utils';

export const authGuard: CanActivateFn = async (route, state) => {
  const msalService = inject(MsalService);
  const msalInitService = inject(MsalInitService);
  const router = inject(Router);

  try {
    // Ensure MSAL is initialized first
    await msalInitService.ensureInitialized();

    // Now handle redirect promise. A stale-cache relic here must not veto the
    // account checks below — it throws even when the user holds a valid
    // session. A genuine authentication failure is rethrown to the outer
    // catch, which sends the user to /login rather than admitting them on a
    // cached account whose first API call would 401.
    let response = null;
    try {
      response = await msalService.instance.handleRedirectPromise();
    } catch (error) {
      if (!isStaleAuthCacheError(error)) {
        throw error;
      }
      console.warn('Ignoring stale auth cache entry in auth guard:', error);
    }

    if (response) {
      // User just completed login via redirect, set the active account
      msalService.instance.setActiveAccount(response.account);
      return true;
    }

    // Check if there's an active account
    const account = msalService.instance.getActiveAccount();

    if (account) {
      return true;
    } else {
      // Check if there are any accounts in cache (might be from a previous session)
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        // Set the first account as active
        msalService.instance.setActiveAccount(accounts[0]);
        return true;
      }

      // No accounts found, redirect to login
      // Store the attempted URL for redirecting after login
      sessionStorage.setItem('redirectUrl', state.url);
      router.navigate(['/login']);
      return false;
    }
  } catch (error: any) {
    // Only log non-MSAL initialization errors
    if (!error?.message?.includes('uninitialized_public_client_application')) {
      console.error('Error in auth guard:', error);
    }
    // Store the attempted URL for redirecting after login
    sessionStorage.setItem('redirectUrl', state.url);
    router.navigate(['/login']);
    return false;
  }
};
